import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useI18n } from '@/lib/i18n'
import { trpcClient } from '@/lib/trpc'
import { Globe, Loader2, Mail, RefreshCw, Send } from 'lucide-react'
import { useState } from 'react'

type ResultState = {
  success: boolean
  message: string
  details?: { sent: number, failed: number }
}

type LanguageSyncResult = {
  success: boolean
  message: string
  details?: { totalUsers: number, languageBreakdown: Record<string, number> }
}

export function EmailServiceSection() {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('') // HTML/Markdown content
  const [recipientList, setRecipientList] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<ResultState | null>(null)
  const [mode, setMode] = useState<'list' | 'bulk'>('list')
  const [showBulkConfirm, setShowBulkConfirm] = useState(false)
  const [bulkLanguage, setBulkLanguage] = useState<'all' | 'zh' | 'non-zh'>('all')
  const { t } = useI18n()

  // User language sync state
  const [syncLoading, setSyncLoading] = useState(false)
  const [syncResult, setSyncResult] = useState<LanguageSyncResult | null>(null)

  const handleSend = async (targetMode: 'list' | 'bulk') => {
    if (!subject || !body) {
      setResult({ success: false, message: 'Subject and Body are required' })
      return
    }

    if (targetMode === 'list' && !recipientList) {
      setResult({ success: false, message: 'Recipient list is required' })
      return
    }

    if (targetMode === 'bulk' && !showBulkConfirm) {
      setMode('bulk')
      setShowBulkConfirm(true)
      return
    }

    setShowBulkConfirm(false)

    setLoading(true)
    setResult(null)
    setMode(targetMode)

    try {
      // Split by comma or newline and clean
      const specificEmails = targetMode === 'list'
        ? recipientList.split(/[\n,]/).map(e => e.trim()).filter(Boolean)
        : undefined

      const response = await trpcClient.batchRouter.sendMarketingEmailBatch.mutate({
        subject,
        htmlBody: body,
        specificEmails,
        targetLanguage: targetMode === 'bulk' && bulkLanguage !== 'all'
          ? bulkLanguage
          : undefined,
      })

      setResult({
        success: response.success,
        message: response.message,
        details: response.details,
      })
    }
    catch (error: unknown) {
      const message
        = error instanceof Error ? error.message : 'Failed to send email'
      setResult({ success: false, message })
    }
    finally {
      setLoading(false)
    }
  }

  const handleSyncUserLanguages = async () => {
    setSyncLoading(true)
    setSyncResult(null)
    try {
      const response = await trpcClient.batchRouter.updateUserLanguages.mutate()
      setSyncResult({
        success: response.success,
        message: response.message,
        details: response.details,
      })
    }
    catch (error: unknown) {
      const message
        = error instanceof Error ? error.message : 'Failed to sync user languages'
      setSyncResult({ success: false, message })
    }
    finally {
      setSyncLoading(false)
    }
  }

  return (
    <div className="container mx-auto max-w-4xl space-y-6 py-8">
      {/* User Language Sync Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="size-5" />
            User Language Sync
          </CardTitle>
          <CardDescription>
            Detect each user's preferred language based on their latest game. Sets the exact game language on the user record. Runs automatically daily.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleSyncUserLanguages}
            disabled={syncLoading}
          >
            {syncLoading ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 size-4" />
            )}
            <span className="ml-2">Sync User Languages Now</span>
          </Button>

          {syncResult && (
            <div className={`rounded-lg p-4 ${syncResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              <p className="font-semibold">{syncResult.message}</p>
              {syncResult.details && (
                <div className="mt-2 text-sm">
                  <span>Total: {syncResult.details.totalUsers}</span>
                  <div className="mt-1 flex flex-wrap gap-x-4">
                    {Object.entries(syncResult.details.languageBreakdown).map(([lang, count]) => (
                      <span key={lang}>{lang}: {count}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Service Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="size-5" />
            Email Service
          </CardTitle>
          <CardDescription>
            Send marketing emails to users. Use simple Markdown or HTML.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Subject</Label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Enter email subject"
            />
          </div>

          <div className="space-y-2">
            <Label>Email Body (HTML supported)</Label>
            <Textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="<h1>Hello!</h1><p>This is a marketing email.</p>"
              className="min-h-[200px] font-mono"
            />
          </div>

          <div className="space-y-4 border-t pt-4">
            {/* Recipient List Row */}
            <div className="space-y-2">
              <Label>Recipients (comma or new line separated)</Label>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                <Textarea
                  value={recipientList}
                  onChange={e => setRecipientList(e.target.value)}
                  placeholder="alice@example.com, bob@example.com"
                  className="min-h-[80px] flex-1 font-mono"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => handleSend('list')}
                  disabled={loading}
                  className="mb-1"
                >
                  {loading && mode === 'list' ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 size-4" />
                  )}
                  <span className="ml-2">Send to List</span>
                </Button>
              </div>
            </div>

            {/* Bulk Row */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center gap-3">
                <Label className="whitespace-nowrap">Target Language:</Label>
                <select
                  value={bulkLanguage}
                  onChange={e => setBulkLanguage(e.target.value as 'all' | 'zh' | 'non-zh')}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="all">All Users</option>
                  <option value="zh">🇨🇳 Chinese-speaking Users</option>
                  <option value="non-zh">🌍 Non-Chinese Users</option>
                </select>
              </div>
              <Button
                type="button"
                variant="destructive"
                className="w-full sm:w-auto"
                onClick={() => {
                  handleSend('bulk')
                }}
                disabled={loading}
              >
                {loading && mode === 'bulk' ? (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                ) : (
                  <Mail className="mr-2 size-4" />
                )}
                <span className="ml-2">
                  {bulkLanguage === 'all'
                    ? 'Send to All Users'
                    : bulkLanguage === 'zh'
                      ? 'Send to 🇨🇳 Chinese-speaking Users'
                      : 'Send to 🌍 Non-Chinese Users'}
                </span>
              </Button>
            </div>
          </div>

          {showBulkConfirm && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bulk-email-confirm-title"
            >
              <div className="mx-4 w-full max-w-md rounded-lg border bg-background p-6">
                <h3 id="bulk-email-confirm-title" className="mb-4 text-lg font-semibold">
                  {bulkLanguage === 'all'
                    ? 'Send to all users?'
                    : bulkLanguage === 'zh'
                      ? 'Send to all 🇨🇳 Chinese-speaking users?'
                      : 'Send to all 🌍 Non-Chinese users?'}
                </h3>
                <p className="mb-6 text-muted-foreground">
                  {bulkLanguage === 'all'
                    ? 'This will send the email to every user. This action cannot be undone.'
                    : bulkLanguage === 'zh'
                      ? 'This will send the email to all users whose language is Chinese (zh). This action cannot be undone.'
                      : 'This will send the email to all users whose language is NOT Chinese. This action cannot be undone.'}
                </p>
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowBulkConfirm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => handleSend('bulk')}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      'Confirm Send'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {result && (
            <div className={`rounded-lg p-4 ${result.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              <p className="font-semibold">{result.message}</p>
              {result.details !== undefined ? (
                <div className="mt-2 text-sm">
                  <span>Sent: {result.details.sent}, Failed: {result.details.failed}</span>
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
